/**
 * One source of truth for "does the Mindtale intro run on this load?".
 *
 * Two places have to agree on the answer: the inline pre-paint script in
 * `app/layout.tsx` (which decides whether the server-rendered curtain stays
 * up) and `components/LoadingWrapper.tsx` (which decides whether the animated
 * intro mounts). They used to carry two hand-copied conditions that had
 * already drifted — the inline script's `indexOf("intro=1") < 0` test also
 * matched the substring inside `nointro=1`, so the Lighthouse escape hatch
 * left the curtain up while the wrapper skipped the intro. Both now derive
 * from the constants below.
 *
 * The intro is a brand moment for someone arriving at the front door. It is
 * not for someone who landed on an article from a search result: they came
 * for an answer, and the first two seconds are when they decide whether to
 * stay or go back to Google. So it runs on the homepage, on a direct visit,
 * once per session.
 */

/** Hosts that mean "this visitor arrived from a search result". */
export const SEARCH_REFERRER_PATTERN =
  "google\\.|bing\\.|duckduckgo|yahoo|yandex|ecosia|startpage|search\\.brave|perplexity";

/** The only path the intro is allowed on. */
export const INTRO_PATH = "/";

/** How long the intro holds the screen, in ms. */
export const INTRO_DURATION_MS = 1200;

/** sessionStorage key marking "this session has already seen it". */
export const INTRO_SEEN_KEY = "mindtale_intro_seen";

/**
 * The inline script, injected before first paint in `app/layout.tsx`. It adds
 * `.intro-skip` (which hides the curtain via CSS) whenever the intro will not
 * run. Kept as a string because it must execute before hydration; it mirrors
 * {@link shouldSkipIntro} exactly.
 */
export const INTRO_GATE_SCRIPT = `(function(){var d=document.documentElement;try{var s=location.search,p=location.pathname;var forced=s.indexOf("intro=1")>-1&&s.indexOf("nointro=1")<0;var skip=s.indexOf("nointro=1")>-1||p!=="${INTRO_PATH}"||new RegExp("${SEARCH_REFERRER_PATTERN}","i").test(document.referrer||"")||!!sessionStorage.getItem("${INTRO_SEEN_KEY}")||matchMedia("(prefers-reduced-motion: reduce)").matches;if(!forced&&skip){d.classList.add("intro-skip")}}catch(e){d.classList.add("intro-skip")}})();`;

/**
 * Client-side twin of {@link INTRO_GATE_SCRIPT}. Returns true when the intro
 * must not run on this load. Call only in the browser.
 */
export function shouldSkipIntro(pathname: string): boolean {
  const search = window.location.search;
  const forced = search.includes("intro=1") && !search.includes("nointro=1");
  if (forced) return false;

  return (
    search.includes("nointro=1") ||
    pathname !== INTRO_PATH ||
    new RegExp(SEARCH_REFERRER_PATTERN, "i").test(document.referrer || "") ||
    !!sessionStorage.getItem(INTRO_SEEN_KEY) ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
